import { useEffect, useState, useRef, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import useInView from '../../hooks/useInView';

const SERVICES = [
  'SEO', 'Social Media Marketing', 'Performance Marketing',
  'Media Buying', 'Paid Advertising', 'Branding',
  'Website Development', 'Not sure — need guidance',
];

const INFO_ITEMS = [
  {
    icon: 'ri-mail-line',
    label: 'Email Us',
    value: 'info@blueglobemedia.com',
    href: 'mailto:info@blueglobemedia.com',
    color: 'text-brand-blue',
    bg: 'bg-blue-50 border-blue-100',
  },
  {
    icon: 'ri-time-line',
    label: 'Response Time',
    value: 'Within 1 business day',
    href: null,
    color: 'text-violet-500',
    bg: 'bg-violet-50 border-violet-100',
  },
  {
    icon: 'ri-global-line',
    label: 'We Work With',
    value: 'Businesses across India',
    href: null,
    color: 'text-orange-500',
    bg: 'bg-orange-50 border-orange-100',
  },
];

function ContactHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 80); return () => clearTimeout(t); }, []);
  return (
    <section className="relative bg-white pt-32 pb-16 overflow-hidden border-b border-gray-100">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-transparent pointer-events-none"></div>
      <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6">
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.7s ease 100ms, transform 0.7s ease 100ms' }}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-blue border border-brand-blue/30 bg-brand-blue/10 px-4 py-1.5 rounded-full mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-blue animate-pulse"></span>
            Get In Touch
          </span>
          <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-gray-900 leading-tight max-w-3xl mb-6">
            Let's Talk About<br />Your Business
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl leading-relaxed">
            Whether you have a clear brief or are still figuring out where to start — we're happy to have an honest conversation.
          </p>
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.value) data.append(el.name, el.value);
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d73accqfp7tlt849ngl0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setStatus('success'); form.reset(); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center text-center py-14">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-brand-blue/15 border border-brand-blue/25 mb-5">
          <i className="ri-checkbox-circle-line text-2xl text-brand-blue"></i>
        </div>
        <h3 className="font-display font-bold text-gray-900 text-xl mb-2">Message Received</h3>
        <p className="text-gray-500 text-sm leading-relaxed max-w-xs mb-6">
          Thanks for reaching out. We'll review your message and get back to you within 1 business day.
        </p>
        <button className="whitespace-nowrap text-brand-blue text-sm font-medium cursor-pointer hover:underline" onClick={() => setStatus('idle')}>
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} data-readdy-form className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[12px] font-medium text-gray-600 mb-1.5">Full Name *</label>
          <input type="text" name="name" required placeholder="Your name" className="w-full bg-gray-50 border border-gray-200 focus:border-brand-blue/60 text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 outline-none transition-colors duration-200" />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-gray-600 mb-1.5">Email Address *</label>
          <input type="email" name="email" required placeholder="you@company.com" className="w-full bg-gray-50 border border-gray-200 focus:border-brand-blue/60 text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 outline-none transition-colors duration-200" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[12px] font-medium text-gray-600 mb-1.5">Phone Number</label>
          <input type="tel" name="phone" placeholder="+91 XXXXX XXXXX" className="w-full bg-gray-50 border border-gray-200 focus:border-brand-blue/60 text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 outline-none transition-colors duration-200" />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-gray-600 mb-1.5">Business Name</label>
          <input type="text" name="business" placeholder="Your business name" className="w-full bg-gray-50 border border-gray-200 focus:border-brand-blue/60 text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 outline-none transition-colors duration-200" />
        </div>
      </div>
      <div>
        <label className="block text-[12px] font-medium text-gray-600 mb-1.5">Service You're Interested In</label>
        <select name="service" defaultValue="" className="w-full bg-gray-50 border border-gray-200 focus:border-brand-blue/60 text-gray-700 text-sm rounded-xl px-4 py-3 outline-none transition-colors duration-200 cursor-pointer">
          <option value="" disabled>Select a service</option>
          {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-[12px] font-medium text-gray-600 mb-1.5">
          Tell Us More <span className="text-gray-400">(max 500 chars)</span>
        </label>
        <textarea name="message" rows={4} maxLength={500} placeholder="Brief description of your business and what you're looking to achieve..." className="w-full bg-gray-50 border border-gray-200 focus:border-brand-blue/60 text-gray-900 text-sm placeholder-gray-400 rounded-xl px-4 py-3 outline-none transition-colors duration-200 resize-none" />
      </div>
      {status === 'error' && <p className="text-red-500 text-xs">Something went wrong. Please try again.</p>}
      <button
        type="submit"
        disabled={status === 'sending'}
        className="btn-primary w-full text-sm py-3.5 rounded-xl"
      >
        {status === 'sending' ? (<><i className="ri-loader-4-line animate-spin"></i>Sending...</>) : (<>Send Message<i className="ri-send-plane-line"></i></>)}
      </button>
      <p className="text-center text-gray-400 text-[11px]">We'll get back to you within 1 business day.</p>
    </form>
  );
}

function ContactSection() {
  const { ref, visible } = useInView(0.1);
  return (
    <section className="py-20 md:py-28 bg-gray-50">
      <div className="max-w-site mx-auto px-4 sm:px-6">
        <div
          ref={ref}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.65s ease, transform 0.65s ease' }}
        >
          {/* Left */}
          <div>
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-brand-blue mb-6">Start the Conversation</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-gray-900 leading-tight mb-5">
              Fill in the Form<br />and We'll Be in Touch
            </h2>
            <p className="text-gray-600 text-[15px] leading-relaxed mb-8">
              Tell us a bit about your business and what you're looking to achieve. There's no pitch call, no commitment required — just an open conversation about whether we can help.
            </p>

            <div className="space-y-4 mb-8">
              {INFO_ITEMS.map(item => (
                <div key={item.label} className="flex items-center gap-4 bg-white border border-gray-100 rounded-xl p-4">
                  <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg border ${item.bg}`}>
                    <i className={`${item.icon} text-lg ${item.color}`}></i>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-gray-900 text-sm font-semibold hover:text-brand-blue transition-colors cursor-pointer">{item.value}</a>
                    ) : (
                      <p className="text-gray-900 text-sm font-semibold">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl overflow-hidden w-full h-[200px]">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20professional%20office%20workspace%20with%20dark%20minimal%20desk%20setup%20laptop%20screen%20glowing%20soft%20warm%20lighting%20plant%20clean%20organized%20contemporary%20interior%20photography%20editorial%20style%20neutral%20tones%20dark%20walls%20wooden%20desk&width=700&height=400&seq=bgm_cta_office_001&orientation=landscape"
                alt="Blue Globe Media workspace"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-white border border-gray-100 rounded-2xl p-7 md:p-9">
            <h3 className="font-display font-bold text-gray-900 text-xl mb-6">Tell us about your project</h3>
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactFAQ() {
  const { ref, visible } = useInView(0.2);
  const FAQS = [
    { q: 'Do you work with businesses outside of a specific city or region?', a: 'Yes — we work with businesses across India. Most of our communication happens over calls and email, so location isn\'t a barrier.' },
    { q: 'What\'s the minimum budget needed to work with you?', a: 'It depends on the services and scope. We\'d rather discuss your goals first and then recommend what\'s realistic for your budget — we don\'t have a fixed minimum.' },
    { q: 'How long before we see results?', a: 'It depends on the channel. Paid advertising can show early results within weeks. SEO typically takes 3-6 months to show meaningful traction. We set honest expectations from the start.' },
    { q: 'Do you require long-term contracts?', a: 'We don\'t believe in locking clients in. We work on a monthly basis and let the results speak for themselves.' },
  ];
  return (
    <section className="py-20 md:py-24 bg-white border-t border-gray-100">
      <div className="max-w-site mx-auto px-4 sm:px-6">
        <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}>
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-brand-blue mb-6">Common Questions</span>
          <h2 className="font-display font-bold text-3xl text-gray-900 mb-10">Frequently Asked</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {FAQS.map(faq => (
              <div key={faq.q} className="bg-gray-50 border border-gray-100 rounded-xl p-6">
                <p className="font-display font-semibold text-gray-900 text-sm mb-2.5 leading-snug">{faq.q}</p>
                <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-gray-500 text-sm">
            Have a different question?{' '}
            <a href="mailto:info@blueglobemedia.com" className="text-brand-blue font-medium hover:underline cursor-pointer">Email us directly.</a>
          </p>
        </div>
      </div>
    </section>
  );
}

export default function ContactPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <ContactHero />
      <ContactSection />
      <ContactFAQ />
      <Footer />
    </div>
  );
}
